import { LoadCreatedEvent } from '@rematter/frontend-lib-api-client/grpc/trade/load_events_pb';
import { LoadStatus } from '@rematter/frontend-lib-api-client/grpc/trade/load_pb';
import {
  BadgeField,
  Col,
  FieldDisplay,
  humanReadableDateTime,
  IDField,
  ThemeColor,
  TinyTable,
} from '@rematter/ui-web';

import { loadStatusLabels, salesItemLineTypeLabels } from '../enumLabels';

import { protoTimestampToZonedDateTimeString } from '@/audit-log/_components/_shared';
import { getLocale } from '~/shared/utils/locale';

const getStatusColor = (status: LoadStatus): ThemeColor => {
  switch (status) {
    case LoadStatus.PENDING:
      return 'warning';
    case LoadStatus.SHIPPED:
      return 'teal';
    case LoadStatus.RECEIVED:
      return 'primary';
    case LoadStatus.RECONCILED:
      return 'success';
    case LoadStatus.UNASSIGNED:
      return 'neutral';
    case LoadStatus.VOIDED:
      return 'failure';
    default:
      return 'neutral';
  }
};

type LoadCreatedDetailProps = {
  event: LoadCreatedEvent;
};

export const LoadCreatedDetail = ({ event }: LoadCreatedDetailProps) => {
  const load = event.load;

  if (!load) {
    return (
      <Col className="@LoadCreatedDetail items-center justify-center p-4 text-neutral/40">
        <span>No data available</span>
      </Col>
    );
  }

  const shippedDateStr = protoTimestampToZonedDateTimeString(load.shippedDate);

  return (
    <Col className="@LoadCreatedDetail gap-3" gap3>
      <Col className="rounded-md border border-neutral/10 bg-white p-3" gap2>
        <span className="text-xs font-medium text-neutral/60">
          Load Details
        </span>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <IDField label="ID" value={load.id} />
          <FieldDisplay
            label="Document Number"
            value={String(load.documentNumber)}
          />
          <BadgeField
            label="Status"
            value={loadStatusLabels[load.status]}
            color={getStatusColor(load.status)}
          />
          <IDField label="Booking ID" value={load.bookingId} />
          <IDField label="Sales Order ID" value={load.salesOrderId} />
          <IDField label="Facility ID" value={load.facilityId} />
          <FieldDisplay
            label="Expected Ship Date"
            value={load.expectedShipDate}
          />
          {shippedDateStr && (
            <FieldDisplay
              label="Shipped Date"
              value={humanReadableDateTime(shippedDateStr, {
                locale: getLocale(),
              })}
            />
          )}
          <FieldDisplay label="Container Number" value={load.containerNumber} />
          <FieldDisplay label="Seal Number" value={load.sealNumber} />
        </div>
      </Col>

      {load.lineItems && load.lineItems.length > 0 && (
        <Col className="rounded-md border border-neutral/10 bg-white p-3" gap1>
          <span className="mb-2 text-xs font-medium text-neutral/60">
            Line Items ({load.lineItems.length})
          </span>
          <TinyTable
            rowKey={(item) => item.id ?? String(Math.random())}
            columns={[
              {
                key: 'itemId',
                label: 'Item ID',
                cell: (item) => (
                  <span className="font-mono text-xs">
                    {item.itemId ?? '-'}
                  </span>
                ),
              },
              {
                key: 'type',
                label: 'Type',
                width: 100,
                cell: (item) => salesItemLineTypeLabels[item.type] ?? '-',
              },
              {
                key: 'pricingUnit',
                label: 'Pricing Unit',
                width: 120,
                cell: (item) => item.pricingUnit ?? '-',
              },
            ]}
            data={load.lineItems}
            isLoading={false}
          />
        </Col>
      )}
    </Col>
  );
};
